/**
 * Product Communication Layer Component
 *
 * Slots:
 *  - visual: Should contain an image 64px x 64px in size.
 *  - content: Should contain a headline (H1), a paragraph of text (P) and a link (A)
 *
 *  Attributes:
 *   - close-button-title: Overrides the title attribute of the close (X) button. Default: Schließen
 *   - background-color: Overrides the default white background color. Supported values:
 *     - gray: Gray background color
 *     - grey: Same as above but more British
 *
 *  Events:
 *  - close-button-click: Fired when the close (X) button is clicked
 **/
export class PccPcl extends HTMLElement {
  static observedAttributes = ['close-button-title']

  protected template = `
<template id="pcc-pcl-template">
<link rel="stylesheet" href="https://s.uicdn.com/mampkg/@mamdev/core-lab.libs.lux@%5E2/lux.css">
<style>
:host {
  box-sizing: border-box;
  display: grid;
  grid-template-columns: min-content auto;
  align-items: center;
  grid-gap: 8px;
  padding: 12px 8px 10px;
  background-color: var(--lux-color-d);
}

:host([background-color=gray]),
:host([background-color=grey]) {
  background-color: var(--lux-color-c-l94);
}

.visual-container {
  display: grid;
  align-items: center;
  justify-items: center;
}

.pcl-close-button {
  float: right;
  --lux-interactive-icon__url: url('https://s.uicdn.com/mampkg/@mamdev/fd.libs.lux.icons/mono/close.svg');
}

::slotted(*[slot=visual]) {
  width: 64px;
  height: 64px;
}

::slotted(h1) {
  margin: 0 28px 4px 0;
  color: var(--lux-color-c-l15);
}

::slotted(p) {
  padding: 0;
  margin: 0 0 4px 0;
  color: var(--lux-color-c);
}

::slotted(a),
::slotted(pcc-iac-goto) {
  padding: 0;
  margin: 0;
}

::slotted([class*=lux-button]) {
  text-decoration: none;
}

</style>
<section class="visual-container">
  <slot name="visual"></slot>
</section>
<section class="content-container">
  <button class="pcl-close-button lux-interactive-icon-medium" title="Schließen" type="button" data-button-type="close"></button>
  <slot name="content"></slot>
</section>
</template>
  `

  constructor() {
    super()
    this.classList.add('lux-typography-body-1')
    this.attachShadow({ mode: 'open' })

    const tempEl = document.createElement('div')
    tempEl.innerHTML = this.template
    const template = tempEl.querySelector('#pcc-pcl-template') as HTMLTemplateElement
    this.shadowRoot.appendChild(template.content.cloneNode(true))

    this.shadowRoot.querySelectorAll<HTMLButtonElement>('[data-button-type=close]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.dispatchEvent(new Event('close-button-click'))
      })
    })

    this.shadowRoot.querySelectorAll('slot').forEach(slot => {
      slot.addEventListener('slotchange', () => {
        slot.assignedNodes().forEach(node => {
          if (node instanceof HTMLElement) {
            this.setClasses(node)
          }
        })
      })
    })

    this.setAttribute('ready', '')
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string) {
    if (name === 'close-button-title') {
      this.setCloseButtonTitle(newValue)
    }
  }

  protected setCloseButtonTitle(title: string) {
    this.shadowRoot?.querySelectorAll<HTMLButtonElement>('[data-button-type=close]').forEach(btn => {
      btn.setAttribute('title', title)
    })
  }

  /**
   * Apply Lux classes to slotted elements to enforce consistent visuals.
   * @param el
   * @protected
   */
  protected setClasses(el: HTMLElement) {
    const cssClasses = el.getAttribute('class') || ''
    if (el.tagName.toLowerCase() !== 'h1'  || cssClasses.includes('lux-typography')) {
      return
    }

    el.classList.add('lux-typography-headline-3')
  }

}

window.customElements.define('pcc-pcl', PccPcl)
